import { getAPIURL } from "./config";

const handleError = (response, errorText, defaultMsg) => {
  let message = defaultMsg;
  if (response.status === 422) {
    try {
      const err = JSON.parse(errorText);
      const details = Array.isArray(err.detail)
        ? err.detail.map((d) => d.msg || JSON.stringify(d)).join("; ")
        : err.detail;
      if (details) message = `Ошибка валидации: ${details}`;
    } catch {
      if (errorText) message += `. ${errorText}`;
    }
  } else if (errorText) {
    message += `. ${errorText}`;
  }
  throw new Error(message);
};

/**
 * Список пользователей с отчётами по ЭП за месяц
 * GET /sketch_design/payments_progress/users?month=&year=
 */
export const getPaymentsProgressUsers = async (month, year) => {
  const response = await fetch(
    `${getAPIURL()}sketch_design/payments_progress/users?month=${month}&year=${year}`,
    {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "include",
    },
  );
  if (!response.ok) {
    const errorText = await response.text();
    handleError(
      response,
      errorText,
      `Ошибка загрузки списка пользователей: ${response.status} ${response.statusText}`,
    );
  }
  return response.json();
};

/**
 * Проекты, по которым работал пользователь за месяц
 * GET /sketch_design/payments_progress/users/projects?month=&year=&user_id=
 */
export const getPaymentsProgressUserProjects = async (month, year, userId) => {
  const response = await fetch(
    `${getAPIURL()}sketch_design/payments_progress/users/projects?month=${month}&year=${year}&user_id=${userId}`,
    {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "include",
    },
  );
  if (!response.ok) {
    const errorText = await response.text();
    handleError(
      response,
      errorText,
      `Ошибка загрузки проектов: ${response.status} ${response.statusText}`,
    );
  }
  return response.json();
};

/**
 * Список проектов с отчётами по ЭП за месяц
 * GET /sketch_design/payments_progress/projects?month=&year=
 */
export const getPaymentsProgressProjects = async (month, year) => {
  const response = await fetch(
    `${getAPIURL()}sketch_design/payments_progress/projects?month=${month}&year=${year}`,
    {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "include",
    },
  );
  if (!response.ok) {
    const errorText = await response.text();
    handleError(
      response,
      errorText,
      `Ошибка загрузки списка проектов: ${response.status} ${response.statusText}`,
    );
  }
  return response.json();
};

/**
 * Пользователи, работавшие по проекту за месяц
 * GET /sketch_design/payments_progress/projects/users?month=&year=&project_id=
 */
export const getPaymentsProgressProjectUsers = async (
  month,
  year,
  projectId,
) => {
  const response = await fetch(
    `${getAPIURL()}sketch_design/payments_progress/projects/users?month=${month}&year=${year}&project_id=${projectId}`,
    {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "include",
    },
  );
  if (!response.ok) {
    const errorText = await response.text();
    handleError(
      response,
      errorText,
      `Ошибка загрузки пользователей проекта: ${response.status} ${response.statusText}`,
    );
  }
  return response.json();
};

/**
 * Статистика по видам работ пользователя по проекту (с корректировками)
 * GET /sketch_design/payments_progress/get?month=&year=&user_id=&project_id=
 */
export const getPaymentsProgressGet = async (
  month,
  year,
  userId,
  projectId,
) => {
  const response = await fetch(
    `${getAPIURL()}sketch_design/payments_progress/get?month=${month}&year=${year}&user_id=${userId}&project_id=${projectId}`,
    {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "include",
    },
  );
  if (!response.ok) {
    const errorText = await response.text();
    handleError(
      response,
      errorText,
      `Ошибка загрузки статистики: ${response.status} ${response.statusText}`,
    );
  }
  return response.json();
};

/**
 * Обновить корректировку
 * PATCH /sketch_design/payments_progress/correction/update
 * @param {{ id: number, month: number, year: number, completion_percentage_after_correction?: number, completion_sum_after_correction?: number, comment?: string }} body
 */
export const updatePaymentsProgressCorrection = async (body) => {
  const normalizedId = Number(body?.id ?? body?.correction_id);
  const payload = {
    ...body,
    id: normalizedId,
    correction_id: normalizedId,
  };

  const response = await fetch(
    `${getAPIURL()}sketch_design/payments_progress/correction/update`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
      body: JSON.stringify(payload),
    },
  );
  if (!response.ok) {
    const errorText = await response.text();
    handleError(
      response,
      errorText,
      `Ошибка обновления корректировки: ${response.status} ${response.statusText}`,
    );
  }
  return response.json();
};

/**
 * Создать согласование по Прогрессу выплат
 * POST /sketch_design/approvals/payments/create?year=&month=&user_id=
 * Body: number[] (id согласующих)
 */
export const createPaymentsProgressApproval = async (
  year,
  month,
  userId,
  approverIds,
) => {
  const response = await fetch(
    `${getAPIURL()}sketch_design/approvals/payments/create?year=${year}&month=${month}&user_id=${userId}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
      body: JSON.stringify(approverIds),
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    handleError(
      response,
      errorText,
      `Ошибка отправки на согласование: ${response.status} ${response.statusText}`,
    );
  }

  return response.json();
};

/**
 * Получить согласование по Прогрессу выплат
 * GET /sketch_design/approvals/payments/get?year=&month=&user_id=
 */
export const getPaymentsProgressApproval = async (year, month, userId) => {
  const response = await fetch(
    `${getAPIURL()}sketch_design/approvals/payments/get?year=${year}&month=${month}&user_id=${userId}`,
    {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "include",
    },
  );

  if (response.status === 404 || response.status === 422) {
    return null;
  }

  if (!response.ok) {
    const errorText = await response.text();
    handleError(
      response,
      errorText,
      `Ошибка загрузки согласования выплат: ${response.status} ${response.statusText}`,
    );
  }

  return response.json();
};

/**
 * Получить все согласования выплат, где текущий пользователь является согласующим
 * GET /sketch_design/approvals/payments/approvals_by_approver
 */
export const getPaymentsProgressApprovalsByApprover = async () => {
  const response = await fetch(
    `${getAPIURL()}sketch_design/approvals/payments/approvals_by_approver`,
    {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "include",
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    handleError(
      response,
      errorText,
      `Ошибка загрузки согласований выплат по согласующему: ${response.status} ${response.statusText}`,
    );
  }

  return response.json();
};

/**
 * Удалить согласование по Прогрессу выплат
 * DELETE /sketch_design/approvals/payments/delete/{payments_approval_id}
 */
export const deletePaymentsProgressApproval = async (paymentsApprovalId) => {
  const response = await fetch(
    `${getAPIURL()}sketch_design/approvals/payments/delete/${paymentsApprovalId}`,
    {
      method: "DELETE",
      headers: { Accept: "application/json" },
      credentials: "include",
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    handleError(
      response,
      errorText,
      `Ошибка удаления согласования выплат: ${response.status} ${response.statusText}`,
    );
  }

  return response.text();
};
